import { useCurrentUser } from '@cma/app'
import { NotFound, classNames, useBuildHandoffUrl } from '@cma/common'
import { useSsoConnect, useSsoUser } from '@cma/features/sso'
import { CloudCmaHorizontalIcon } from '@cma/icons'
import { HelixIcon } from '@helix/helix-icon'
import {
  arrow_down as ArrowDownIcon,
  arrow_right as ArrowRightIcon,
  spinner as SpinnerIcon,
  wolfie as WolfieIcon
} from '@helix/helix-icon/outlined'
import { useParams, useSearchParams } from 'react-router-dom'

export default function SsoConnect() {
  const { service } = useParams()
  const { data } = useCurrentUser()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token') || ''
  const buildHandoffUrl = useBuildHandoffUrl()
  const { data: ssoUserData } = useSsoUser({ token }, { enabled: !!token })
  const {
    mutate: connect,
    isLoading: isConnecting,
    error: connectError
  } = useSsoConnect({
    async onSuccess(data) {
      await new Promise(() => {
        window.location.href =
          data.partnershipUserConnect?.redirectUrl || buildHandoffUrl('/')
      })
    }
  })
  const ssoName =
    searchParams.get('name') ||
    [
      ssoUserData?.partnershipUser?.firstName,
      ssoUserData?.partnershipUser?.lastName
    ]
      .filter(Boolean)
      .join(' ') ||
    ''
  const ssoEmail =
    searchParams.get('email') || ssoUserData?.partnershipUser?.email || ''
  const ssoOuid = ssoUserData?.partnershipUser?.ouid || ''

  if (!data?.currentUser || !token) {
    return (
      <div className="m-auto mt-16 w-4/5 py-8">
        <NotFound />
      </div>
    )
  }

  return (
    <div className="helix-card helix-p-4">
      <div className="helix-card__body">
        <div className="helix-w-100-percent helix-d-flex helix-justify--center">
          <HelixIcon
            icon={WolfieIcon}
            className="helix-svg-fill--gray-900 mx-auto mb-3 h-14 w-14"
          />
        </div>
        <h1 className="helix-text-gray-900 helix-display-small helix-mb-6 helix-text--center">
          Connect this account
        </h1>
        <div className="helix-d-flex helix-d-sm-block helix-justify--center helix-mb-6">
          <div className="helix-d-sm-flex helix-d-block">
            <div className="helix-card helix-w-300">
              <div className="helix-p-5 helix-pt-6 helix-text--center">
                <CloudCmaHorizontalIcon className="mx-auto mb-4 h-12 w-40 text-cma-500" />
                <div className="helix-body--strong helix-mb-1">
                  {data?.currentUser?.name}
                </div>
                <div className="helix-text-gray-500 helix-small">
                  {data?.currentUser?.email}
                </div>
              </div>
            </div>
            <div className="helix-d-flex helix-text-gray-900 helix-justify--center helix-align--middle helix-p-4">
              <HelixIcon
                icon={ArrowRightIcon}
                className="helix-d-none helix-d-sm-block"
              />
              <HelixIcon
                icon={ArrowDownIcon}
                className="helix-d-block helix-d-sm-none"
              />
            </div>
            <div className="helix-card helix-w-300">
              <div className="helix-p-5 helix-pt-6 helix-text--center">
                <div className="mb-4 flex justify-center">
                  {service === 'rdc' ? (
                    <svg
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 160 23"
                      className="h-12 w-40">
                      <path
                        d="M76.903 20.437c-.574.76-1.4 1.179-2.322 1.179-1.465 0-1.846-.67-1.846-1.842L72.8 8.906h3.568V8.21H72.8V3.407h-.413c-.69 2.968-2.484 4.7-4.58 5.022v.477l2.128-.007s-.064 10.141-.064 10.457c0 1.21.226 1.97.89 2.543.639.54 1.4.792 2.678.792 1.78 0 3.026-.92 3.69-2.035l-.226-.219Z"
                        fill="#000"
                      />
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M91.103 15.26c0-4.577-3.251-7.372-6.974-7.372-3.69 0-6.974 2.795-6.974 7.405s3.251 7.404 6.974 7.404c3.729 0 6.974-2.826 6.974-7.436ZM84.13 22.19c-.794 0-1.684-.252-2.355-.953-1.02-1.082-1.529-3.084-1.529-5.943 0-2.923.51-4.926 1.561-5.943.633-.67 1.53-.953 2.323-.953.761 0 1.652.251 2.323.953 1.051 1.05 1.56 3.052 1.56 5.943 0 2.859-.541 4.893-1.56 5.943-.665.701-1.53.953-2.323.953Z"
                        fill="#000"
                      />
                      <path
                        d="M100.11 7.888c-1.084 0-1.781.412-2.355.889-.639.605-1.239 1.404-1.723 2.897h-.097c0-.117.02-1.008.036-1.83V9.84c.015-.706.029-1.362.029-1.437 0-.096-.065-.193-.226-.193h-4.458v.284h.003c.38.032.766.065 1.113.315.29.187.51.477.574.889.097.412.097 1.59.097 2.099v6.773c0 .824-.032 1.68-.129 2.254-.187.637-.413.856-.858 1.081a3.084 3.084 0 0 1-.858.226v.283h6.594v-.283c-.284-.032-.51-.065-.794-.194-.258-.096-.445-.321-.639-.67-.284-.604-.445-1.267-.445-2.858v-1.828c0-.915.02-1.81.136-2.718.296-2.375 1.49-4.275 2.348-4.275.38 0 .6.38.826.92.258.606.574.812 1.497.812.89 0 1.4-.734 1.4-1.558 0-.605-.194-1.017-.478-1.333-.355-.347-.922-.54-1.593-.54ZM109.768 9.163c-.891.889-1.529 2.891-1.529 5.846 0 3.085.638 4.765 1.529 5.75.574.638 1.464 1.05 2.484 1.05 2.929 0 4.142-2.196 4.645-3.844l.477.16c-.703 2.576-2.484 4.579-5.51 4.579-1.877 0-3.245-.605-4.296-1.494-1.368-1.146-2.42-2.988-2.42-5.91 0-4.134 2.929-7.405 6.716-7.405 2.484 0 3.633.67 4.362 1.397.542.476.858 1.114.858 1.745 0 .856-.542 1.3-1.368 1.3-.922 0-1.432-.605-1.593-1.686-.381-1.713-.859-2.254-2.291-2.254-.793 0-1.522.29-2.064.766Z"
                        fill="#000"
                      />
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M132.897 15.26c0-4.577-3.252-7.372-6.974-7.372-3.691 0-6.975 2.795-6.975 7.405s3.252 7.404 6.975 7.404c3.729 0 6.974-2.826 6.974-7.436Zm-6.974 6.929c-.794 0-1.684-.252-2.355-.953-1.02-1.082-1.529-3.084-1.529-5.943 0-2.923.509-4.926 1.561-5.943.632-.67 1.529-.953 2.323-.953.761 0 1.651.251 2.322.953 1.052 1.05 1.561 3.052 1.561 5.943 0 2.859-.542 4.893-1.561 5.943-.664.701-1.529.953-2.322.953Z"
                        fill="#000"
                      />
                      <path
                        d="M103.587 21.088c0 .892-.725 1.616-1.619 1.616-.895 0-1.62-.724-1.62-1.616 0-.893.725-1.617 1.62-1.617.894 0 1.619.724 1.619 1.617ZM133.226 22.407v-.25c.251-.033.606-.162.826-.252.574-.225.858-.476.987-1.178.096-.54.096-1.365.096-2.286v-6.644c0-.825 0-1.687-.096-2.132-.097-.444-.252-.734-.542-.888-.284-.193-.703-.251-1.084-.283V8.21h4.458c.161 0 .193.065.193.193 0 .082-.016.736-.032 1.384v.006c-.016.636-.032 1.264-.032 1.34h.129c.445-1.113.987-1.809 1.716-2.317.703-.573 1.658-.921 3.026-.921 1.271 0 2.2.348 2.8 1.017.51.541.826 1.21.89 2.286h.097c.542-1.269 1.148-1.938 1.845-2.415.794-.573 1.691-.888 2.929-.888 1.368 0 2.168.348 2.8.985.703.637 1.02 1.622 1.02 3.206v6.548c0 .857.032 1.655.161 2.164.161.605.348.888.826 1.114.059.02.119.042.179.064.228.082.461.166.711.187v.251H150.6v-.283l.04-.01c.424-.091.729-.157.979-.338.316-.225.51-.54.607-.953.129-.54.129-1.397.129-2.382v-6.085c0-.985-.161-1.712-.51-2.253-.348-.541-.922-.824-1.69-.824-1.4 0-2.484.92-3.091 2.607-.316.857-.412 1.906-.412 3.464v3.226l-.002.476v.002c-.005.748-.009 1.38.098 1.776.162.444.349.791.704 1.017.251.129.542.25.954.315v.251h-6.432v-.283c.445-.064.761-.16 1.02-.315.348-.194.541-.477.606-.889.161-.573.194-1.494.194-2.479v-6.117c0-.888-.162-1.59-.381-2.066-.381-.67-.987-.985-1.878-.985-1.496 0-2.483 1.017-3.058 2.64-.348.985-.445 1.97-.445 3.431v3.097c0 .985 0 1.777.162 2.383.096.412.251.701.509.888.252.225.639.348 1.149.412v.27h-6.626Z"
                        fill="#000"
                      />
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M159.103 11.346a.469.469 0 0 1-.077-.258l-.02-.244c-.012-.155-.071-.264-.2-.316.194-.084.246-.244.246-.399 0-.303-.233-.47-.594-.47h-.723v1.693h.297v-.682h.458c.168 0 .22.058.233.245l.012.173c.007.11.02.2.065.258h.303Zm-.651-1.423c.193 0 .296.077.296.238 0 .155-.096.239-.271.239h-.451v-.477h.426Z"
                        fill="#000"
                      />
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M158.368 12.1c.916 0 1.632-.696 1.632-1.578 0-.882-.716-1.578-1.632-1.578-.923 0-1.639.696-1.639 1.578 0 .876.716 1.577 1.639 1.577Zm0-2.853c.735 0 1.296.56 1.296 1.275 0 .714-.561 1.268-1.296 1.268-.742 0-1.303-.553-1.303-1.268s.561-1.275 1.303-1.275Z"
                        fill="#000"
                      />
                      <path
                        d="M65.929 18.976c0 .734 0 1.3.064 1.777.162.67.51.953.859 1.114.283.128.606.225.89.25v.284h-6.555v-.283c.284-.032.639-.129.858-.251.413-.194.73-.445.858-.986.13-.54.13-1.146.13-1.905V4.238c0-.477 0-1.655-.065-2.067-.13-.444-.284-.734-.542-.92-.316-.194-.607-.252-1.116-.284v-.29h4.425c.162 0 .226.065.226.225 0 .275-.006.572-.013.91-.008.448-.019.968-.019 1.602v15.562ZM30.026 8.777c.574-.477 1.27-.889 2.355-.889.67 0 1.238.194 1.593.541.284.316.478.728.478 1.333 0 .824-.51 1.558-1.4 1.558-.923 0-1.24-.206-1.497-.811-.226-.54-.445-.92-.826-.92-.858 0-2.052 1.899-2.348 4.274-.116.908-.136 1.803-.136 2.718v1.828c0 1.59.162 2.254.445 2.859.194.348.381.573.639.67.284.128.51.16.794.193v.283h-6.594v-.283c.252-.033.574-.097.858-.226.445-.225.671-.444.858-1.081.097-.573.13-1.43.13-2.254v-6.773c0-.509 0-1.687-.098-2.1a1.252 1.252 0 0 0-.574-.888c-.347-.25-.733-.283-1.113-.315h-.003V8.21h4.458c.161 0 .226.097.226.193 0 .075-.014.73-.029 1.437-.017.824-.036 1.716-.036 1.834h.097c.484-1.493 1.084-2.292 1.723-2.897Z"
                        fill="#D92228"
                      />
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M60.065 22.13h.025v.284c-.477.135-1.07.283-1.735.283-.671 0-1.18-.154-1.465-.347-.451-.316-.67-.76-.703-1.745h-.129c-.51 1.05-1.651 2.099-3.722 2.099-2.233 0-3.949-1.243-3.949-3.529 0-.856.252-1.712.923-2.35.804-.752 1.725-1.03 2.83-1.365.295-.09.602-.182.925-.29 2.07-.605 3.025-1.461 3.09-2.446V10.56c0-1.654-.607-2.195-1.942-2.195a2.67 2.67 0 0 0-1.465.412c-.51.348-.67.953-.703 1.841-.032 1.114-.606 1.526-1.529 1.526-.729 0-1.335-.444-1.335-1.268 0-.637.258-1.114.67-1.526.794-.889 2.8-1.462 4.297-1.462 2.833 0 4.84.986 4.84 3.149v8.46c0 1.397.064 2.003.348 2.318.154.22.412.316.729.316Zm-6.555-.45c1.116 0 2.58-.953 2.613-2.575v-5.048c-.502.659-1.434 1.1-2.165 1.444l-.035.017c-.988.412-1.659.76-2.04 1.236-.412.477-.573 1.179-.573 2.132 0 1.97.8 2.794 2.2 2.794ZM47.258 18.126l-.535-.168c-.62 2.016-1.93 3.812-4.581 3.812-1.18 0-2.226-.38-2.961-1.21-.858-.953-1.4-2.891-1.4-5.306 0-.25 0-.431.032-.682h9.329c.064-.412.064-1.063.064-1.417 0-2.414-1.69-5.273-5.76-5.273-3.756 0-6.782 3.116-6.782 7.469 0 2.163.607 4.069 2.007 5.466 1.213 1.236 2.832 1.874 4.871 1.874 3.006 0 4.903-1.816 5.722-4.572h-.006v.007Zm-7.852-8.873c.542-.54 1.271-.824 2.071-.824.89 0 1.433.251 1.942.702.607.605.923 1.623.923 3.27 0 1.237-.194 1.495-1.148 1.495h-5.349c.194-2.1.768-3.94 1.562-4.643ZM19.568 13.4 9.794 3.375 0 13.4h2.987v8.995h13.6V13.4h2.98Zm-6.045-2.627c-.258-.258-.671-.4-1.168-.4-.82 0-1.336.342-1.69.638l-.007.007c-.355.34-.761.804-1.097 1.706l.016-.715.004-.206c.013-.502.019-.934.019-.985 0-.11-.077-.225-.245-.225h-3.18v.27l.083.007c.258.019.503.045.73.206a.81.81 0 0 1 .36.56c.065.277.065 1.088.065 1.436v4.603c0 .47-.013 1.114-.084 1.545-.129.419-.264.548-.542.69-.155.07-.348.122-.568.147l-.09.007v.27h4.748v-.27l-.084-.007a1.59 1.59 0 0 1-.528-.128c-.142-.052-.265-.18-.394-.419-.187-.4-.303-.85-.303-1.944v-1.92c0-.579.026-1.139.045-1.383.213-1.533 1.045-2.762 1.587-2.762.207 0 .342.225.49.585.175.412.42.85 1.123.85.697 0 1.064-.586 1.064-1.172.007-.399-.11-.72-.354-.991Z"
                        fill="#D92228"
                      />
                    </svg>
                  ) : (
                    <HelixIcon
                      icon={WolfieIcon}
                      className="helix-svg-fill--red-900 h-12 w-40"
                    />
                  )}
                </div>
                <div
                  className={classNames('helix-body--strong helix-mb-1', {
                    'helix-text-gray-500 italic': !ssoName
                  })}>
                  {ssoName || 'John Doe'}
                </div>
                <div
                  className={classNames('helix-text-gray-500 helix-small', {
                    'helix-text-gray-400 italic': !ssoEmail
                  })}>
                  {ssoEmail || 'john.doe@example.com'}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="helix-d-flex helix-mb-2 helix-align--middle helix-justify--center">
          <div className="helix-w-400">
            <button
              disabled={isConnecting || !ssoOuid}
              onClick={() => connect({ token, ouid: ssoOuid })}
              className="helix-btn helix-btn--primary helix-w-100-percent helix-d-block helix-text--center hover:no-underline">
              {!isConnecting ? (
                <>
                  Connect to {service === 'rdc' ? 'Realtor.com®' : 'Lone Wolf'}{' '}
                  account
                </>
              ) : (
                <span className="mx-auto block h-5 w-5 animate-spin">
                  <HelixIcon icon={SpinnerIcon} />
                </span>
              )}
            </button>
            {connectError && (
              <div
                role="alert"
                aria-live="polite"
                className="helix-text-red-500 helix-small helix-text--center mt-4">
                {connectError?.message}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
